/**
 * Shared form helpers (phone/email formatting + validation, Sharon
 * location constants). Renamed from n8n.ts 2026-07-11 (N8N-DECOMM) —
 * the name was the last n8n artifact in this repo.
 *
 * The legacy n8n webhook client that used to live here was removed as
 * part of SEC-1/C8: n8n is retired (flows.ops.pkntindustries.com now
 * returns 503) and its calls carried a hardcoded X-Webhook-Key that
 * shipped publicly in the browser bundle. Form submissions now go
 * through JWT-gated Supabase Edge Functions via
 * `supabase.functions.invoke(...)`, so no client-side bearer token is
 * needed. See `src/pages/Refer.tsx` for the referral-submit call.
 */

export function formatPhone(val: string): string {
  let d = val.replace(/\D/g, '');
  // E.164 US numbers arrive as +16173143772 — 11 digits with a leading country code. Without
  // dropping it, the slice-by-position format shifts every group and renders (161) 733-1437
  // (caught live by Peter: 'my phone number is coming out very weird'). Strip the 1, then format.
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function isValidPhone(v: string): boolean {
  return v.replace(/\D/g, '').length >= 10;
}

import { supabase } from './supabase';
import { isCell } from './cell/config';
import { cellGet } from './cell/api';

/**
 * Suite labels for the given location, from the platform `suites`
 * table (off-CRM doctrine + multi-tenancy fix, 2026-07-11 — replaces
 * the hardcoded Sharon 101–128 list that Demo/Brookline Pros were
 * shown and that Spotlight persisted into records). Falls back to
 * the legacy Sharon list only when the query fails or returns empty,
 * so a transient error never renders an empty picker.
 */
export async function fetchSuiteLabels(locationId: string | null): Promise<string[]> {
  const fallback = Array.from({ length: 28 }, (_, i) => `Suite ${101 + i}`);
  if (!locationId) return fallback;
  // CELL (M28): GET /v1/pro-hub/locations/{locationId}/suites — the same read (suite_number, ordered).
  const { data, error } = isCell()
    ? await cellGet<{ rows: Array<{ suite_number: string | number }> }>(`/v1/pro-hub/locations/${encodeURIComponent(locationId)}/suites`)
      .then((r) => ({ data: r.data?.rows ?? null, error: r.error }))
    : await supabase
    .from('suites')
    .select('suite_number')
    .eq('location_id', locationId)
    .order('suite_number');
  if (error || !data || data.length === 0) return fallback;
  return data.map((r) => `Suite ${r.suite_number}`);
}

/** Common areas — platform-generic labels (not per-location today). */
export const COMMON_AREAS = [
  'Common Area — Breakroom',
  'Common Area — Restroom',
  'Common Area — Hallway',
  'Common Area — Lobby',
  'Common Area — Laundry',
];

/** @deprecated legacy aliases — migrate callers to fetchSuiteLabels/COMMON_AREAS. */
export const SHARON_SUITES = Array.from({ length: 28 }, (_, i) => `Suite ${101 + i}`);
export const SHARON_COMMON_AREAS = COMMON_AREAS;
